import React, { useState, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Drawer from "@mui/material/Drawer";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import Chip from "@mui/material/Chip";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Box,
  Button,
  Grid,
  Typography,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { axiosInstance } from "../../utils/axios";

// Helper function to parse event dates which can come in different formats
const parseEventDate = (dateValue) => {
  if (!dateValue) return null;

  // If it's already a Date object
  if (dateValue instanceof Date) return dateValue;

  // If it's a numeric string (timestamp in milliseconds)
  if (typeof dateValue === "string" && /^\d+$/.test(dateValue)) {
    return new Date(parseInt(dateValue, 10));
  }

  // If it's a number
  if (typeof dateValue === "number") {
    return new Date(dateValue);
  }

  // If it's a date-only string (YYYY-MM-DD), parse it without timezone conversion
  if (typeof dateValue === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    const [year, month, day] = dateValue.split("-").map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  }

  // Otherwise try to parse as ISO string or other date format
  const parsed = new Date(dateValue);

  // Check if the date is valid
  if (isNaN(parsed.getTime())) {
    console.error("Invalid date value:", dateValue);
    return null;
  }

  return parsed;
};

const createValidationSchema = (eventData, sessions = []) =>
  Yup.object({
    name: Yup.string().required("Break name is required"),
    breakDate: Yup.date()
      .required("Break date is required")
      .test("within-session-bounds", function (value) {
        if (!eventData || !value) return true;

        const breakDate = new Date(value);
        breakDate.setHours(0, 0, 0, 0);

        // First check if break date is within event bounds
        const eventStartDate = parseEventDate(eventData.startDate);
        const eventEndDate = parseEventDate(eventData.endDate);

        if (!eventStartDate || !eventEndDate) {
          console.error(
            "Failed to parse event dates:",
            eventData.startDate,
            eventData.endDate
          );
          return true; // Skip validation if dates are invalid
        }

        eventStartDate.setHours(0, 0, 0, 0);
        eventEndDate.setHours(23, 59, 59, 999);

        if (breakDate < eventStartDate || breakDate > eventEndDate) {
          return this.createError({
            message: `Break date must be within event dates (${eventStartDate.toDateString()} - ${eventEndDate.toDateString()})`,
          });
        }

        return true;
      }),
    breakTime: Yup.date().required("Break time is required").nullable(),
    duration: Yup.number()
      .required("Duration is required")
      .positive("Duration must be positive"),
    description: Yup.string().required("Description is required"),
  });

function AvailableTracks({ eventId, onTrackSelect }) {
  const [tracks, setTracks] = useState([]);

  useEffect(() => {
    const fetchTracks = async () => {
      try {
        const response = await axiosInstance.post("/graphql", {
          query: `
          query Tracks($eventId: ID!) {
            tracks(eventId: $eventId) {
              id
              name
            }
          }`,
          variables: {
            eventId: eventId,
          },
        });

        if (response.errors) {
          throw new Error(
            response.errors[0]?.message || "Failed to fetch tracks"
          );
        }

        setTracks(response.data.tracks || []);
      } catch (error) {
        console.error("Error fetching tracks:", error);
      }
    };

    if (eventId) {
      fetchTracks();
    }
  }, [eventId]);

  return (
    <Grid item xs={12}>
      <Autocomplete
        multiple
        options={tracks}
        value={[]}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        onChange={(event, newValue) => onTrackSelect(newValue)}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              key={option.id}
              label={option.name}
              {...getTagProps({ index })}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Tracks"
            placeholder="Select Tracks"
            fullWidth
          />
        )}
      />
    </Grid>
  );
}

function BreakTags({ onTagChange }) {
  return (
    <Grid item xs={12}>
      <Autocomplete
        multiple
        freeSolo
        options={[]}
        value={[]}
        onChange={(event, newValue) => onTagChange(newValue)}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip key={index} label={option} {...getTagProps({ index })} />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Tags"
            placeholder="Add Tags"
            fullWidth
          />
        )}
      />
    </Grid>
  );
}

function Break({
  eventId,
  onBreakAdded,
  editMode = false,
  breakToEdit = null,
  onClose,
}) {
  console.log("🚀 Break component initialized:", {
    eventId,
    editMode,
    breakToEdit,
  });

  const [state, setState] = useState({
    right: false,
  });

  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [eventData, setEventData] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  // Fetch event data and sessions for validation
  useEffect(() => {
    if (eventId) {
      const fetchEventData = async () => {
        try {
          setDataLoading(true);
          console.log("🔄 Starting data fetch for eventId:", eventId);

          const response = await axiosInstance.post("/graphql", {
            query: `
              query GetEvent($id: ID!) {
                event(id: $id) {
                  id
                  name
                  startDate
                  endDate
                  startTime
                  endTime
                  timezone
                }
              }
            `,
            variables: { id: eventId },
          });

          console.log("📡 Break - GraphQL response:", response);
          console.log("📡 Break - Response.data:", response.data);

          // Handle different response structures
          let eventData = null;
          if (response.data?.data?.event) {
            eventData = response.data.data.event;
          } else if (response.data?.event) {
            eventData = response.data.event;
          }

          if (eventData) {
            console.log("✅ Break - Event data loaded successfully:");
            console.log(
              "  📅 Event startDate:",
              eventData.startDate,
              "parsed:",
              parseEventDate(eventData.startDate)
            );
            console.log(
              "  📅 Event endDate:",
              eventData.endDate,
              "parsed:",
              parseEventDate(eventData.endDate)
            );
            console.log("  ⏰ Event startTime:", eventData.startTime);
            console.log("  ⏰ Event endTime:", eventData.endTime);
            console.log("  🌍 Event timezone:", eventData.timezone);
            setEventData(eventData);
          } else {
            console.error(
              "❌ Break - No event data found in response:",
              response
            );
            setAlert({
              open: true,
              message: "Failed to load event data. Please refresh the page.",
              severity: "error",
            });
          }

          // Fetch sessions for validation
          const sessionsResponse = await axiosInstance.post("/graphql", {
            query: `
              query GetSessions($eventId: ID) {
                getSessions(eventId: $eventId) {
                  id
                  name
                  sessionDate
                  sessionTime
                  duration
                }
              }
            `,
            variables: { eventId },
          });

          let sessionsData = [];
          if (sessionsResponse.data?.data?.getSessions) {
            sessionsData = sessionsResponse.data.data.getSessions;
          } else if (sessionsResponse.data?.getSessions) {
            sessionsData = sessionsResponse.data.getSessions;
          }

          console.log(
            "📊 Break - Sessions loaded:",
            sessionsData.length,
            "sessions"
          );
          if (sessionsData.length > 0) {
            console.log("📊 Sample session:", sessionsData[0]);
          }
          setSessions(sessionsData);
        } catch (error) {
          console.error("❌ Break - Error fetching data:", error);
          console.error("❌ Break - Error details:", error.response?.data);
          setAlert({
            open: true,
            message:
              "Failed to load event or session data. Please refresh the page.",
            severity: "error",
          });
        } finally {
          setDataLoading(false);
        }
      };

      fetchEventData();
    } else {
      setDataLoading(false);
    }
  }, [eventId]);

  const toggleDrawer = (anchor, open) => (event) => {
    if (
      event &&
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setState({ ...state, [anchor]: open });
  };

  // Create validation schema that updates when eventData or sessions change
  const validationSchema = useMemo(() => {
    console.log(
      "🔄 Creating break validation schema with eventData:",
      eventData,
      "sessions:",
      sessions.length
    );
    if (eventData) {
      console.log("📊 Break validation - Event date range:", {
        start: parseEventDate(eventData.startDate),
        end: parseEventDate(eventData.endDate),
        rawStart: eventData.startDate,
        rawEnd: eventData.endDate,
      });
    }

    // If we don't have eventData yet, or we're in edit mode and data is still loading, use simple validation
    if (!eventData || (editMode && dataLoading)) {
      console.log(
        "⚠️ No eventData available or data loading, using simplified validation schema"
      );
      return Yup.object({
        name: Yup.string().required("Break name is required"),
        breakDate: Yup.date().required("Break date is required").nullable(),
        breakTime: Yup.date().required("Break time is required").nullable(),
        duration: Yup.number()
          .required("Duration is required")
          .positive("Duration must be positive"),
        description: Yup.string().required("Description is required"),
      });
    }

    return createValidationSchema(eventData, sessions);
  }, [eventData, sessions, editMode, dataLoading]);

  const formik = useFormik({
    initialValues: {
      name: breakToEdit?.name || "",
      breakDate: breakToEdit?.breakDate
        ? (() => {
            try {
              console.log("🔧 === BREAK DATE PARSING DEBUG ===");
              console.log(
                "🔧 breakToEdit.breakDate:",
                breakToEdit.breakDate,
                typeof breakToEdit.breakDate
              );

              // Parse the break date - handle timestamp strings, date strings, and Date objects
              if (
                typeof breakToEdit.breakDate === "string" &&
                /^\d+$/.test(breakToEdit.breakDate)
              ) {
                // It's a timestamp string like "1754784000000"
                const timestamp = parseInt(breakToEdit.breakDate, 10);
                const result = new Date(timestamp);
                console.log(
                  "🔧 Parsed timestamp string result:",
                  result,
                  "from timestamp:",
                  timestamp
                );
                if (isNaN(result.getTime())) {
                  console.error("❌ Invalid timestamp, returning null");
                  return null;
                }
                return result;
              } else if (
                typeof breakToEdit.breakDate === "string" &&
                /^\d{4}-\d{2}-\d{2}$/.test(breakToEdit.breakDate)
              ) {
                // It's a date string like "2025-08-24"
                const [year, month, day] = breakToEdit.breakDate
                  .split("-")
                  .map(Number);
                const result = new Date(year, month - 1, day); // month is 0-indexed
                console.log("🔧 Parsed date string result:", result);
                return result;
              } else if (typeof breakToEdit.breakDate === "number") {
                // It's a numeric timestamp
                const result = new Date(breakToEdit.breakDate);
                console.log("🔧 Parsed numeric timestamp result:", result);
                return result;
              } else {
                // It's a Date object or other format
                const result = new Date(breakToEdit.breakDate);
                console.log(
                  "🔧 Parsed as Date result:",
                  result,
                  "isValid:",
                  !isNaN(result.getTime())
                );
                if (isNaN(result.getTime())) {
                  console.error("❌ Invalid date, returning null");
                  return null;
                }
                return result;
              }
            } catch (error) {
              console.error("❌ Error parsing break date:", error);
              return null;
            }
          })()
        : null,
      breakTime: breakToEdit?.breakTime
        ? (() => {
            // Simple parsing - just convert UTC time from backend to local display
            if (
              typeof breakToEdit.breakTime === "string" &&
              /^\d{1,2}:\d{2}(:\d{2})?$/.test(breakToEdit.breakTime)
            ) {
              const [hours, minutes, seconds = 0] = breakToEdit.breakTime
                .split(":")
                .map(Number);

              // Backend stores as UTC, create date with UTC time
              const timeDate = new Date();
              timeDate.setUTCHours(hours, minutes, seconds, 0);

              console.log("Break time initialization:", {
                backend: breakToEdit.breakTime,
                localDisplay: timeDate.toLocaleTimeString(),
              });

              return timeDate;
            }
            return null;
          })()
        : null,
      duration: breakToEdit?.duration?.toString() || "",
      description: breakToEdit?.description || "",
      tracks: breakToEdit?.tracks || [],
      tags: breakToEdit?.tags || [],
    },
    validationSchema: validationSchema,
    enableReinitialize: true, // Allow the form to reinitialize when breakToEdit changes
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        // Check if event data is loaded before allowing submission
        if (!eventData) {
          console.error(
            "❌ Break - Event data not loaded, preventing submission"
          );
          setAlert({
            open: true,
            message: "Event data is still loading. Please wait and try again.",
            severity: "error",
          });
          setSubmitting(false);
          return;
        }

        // Check for real-time validation errors
        const hasRealTimeErrors = Object.values(realTimeErrors).some(
          (error) => error !== null
        );
        if (hasRealTimeErrors) {
          console.error(
            "❌ Break - Real-time validation errors detected, preventing submission"
          );
          const errorMessage = Object.values(realTimeErrors).find(
            (error) => error !== null
          );
          setAlert({
            open: true,
            message: errorMessage,
            severity: "error",
          });
          setSubmitting(false);
          return;
        }

        console.log("✅ Break - Form submission with eventData:", eventData);
        console.log(
          "✅ Break - Form valid:",
          !Object.keys(formik.errors).length
        );
        console.log(
          "✅ Break - Error count:",
          Object.keys(formik.errors).length
        );

        // Double-check that the break date is within event bounds
        const breakDate = new Date(values.breakDate);
        const eventStartDate = parseEventDate(eventData.startDate);
        const eventEndDate = parseEventDate(eventData.endDate);

        eventStartDate.setHours(0, 0, 0, 0);
        eventEndDate.setHours(23, 59, 59, 999);
        breakDate.setHours(0, 0, 0, 0);

        console.log("🔍 Break submission validation:", {
          breakDate,
          eventStartDate,
          eventEndDate,
          isValid: breakDate >= eventStartDate && breakDate <= eventEndDate,
        });

        if (breakDate < eventStartDate || breakDate > eventEndDate) {
          console.error("❌ Break date is outside event bounds!");
          setAlert({
            open: true,
            message: `Break date must be within event dates (${eventStartDate.toDateString()} - ${eventEndDate.toDateString()})`,
            severity: "error",
          });
          setSubmitting(false);
          return;
        }

        setAlert({
          open: false,
          message: "",
          severity: "success",
        });

        // Format the date to YYYY-MM-DD
        const formattedDate = values.breakDate?.toISOString().split("T")[0];

        // Format the time to UTC HH:MM:SS (same as session pattern)
        const formattedTime = values.breakTime
          ? values.breakTime.toISOString().split("T")[1].split(".")[0]
          : null;

        console.log("📝 Break date (ISO for storage):", formattedDate);
        console.log("📝 Break time (UTC for storage):", formattedTime);

        let response;
        if (editMode && breakToEdit) {
          // Update existing break
          console.log("🔧 Updating break with ID:", breakToEdit.id);
          response = await axiosInstance.post("/graphql", {
            query: `
              mutation UpdateBreak($id: ID!, $eventId: ID!, $input: UpdateBreakInput!) {
                updateBreak(id: $id, eventId: $eventId, input: $input) {
                  id
                  name
                  breakDate
                  breakTime
                  duration
                  description
                }
              }
            `,
            variables: {
              id: breakToEdit.id,
              eventId: eventId,
              input: {
                name: values.name,
                breakDate: formattedDate,
                breakTime: formattedTime,
                duration: parseInt(values.duration),
                description: values.description,
                trackIds: values.tracks.map((track) => track.id),
                tags: values.tags,
              },
            },
          });

          if (response.data?.errors) {
            throw new Error(
              response.data.errors[0]?.message || "Error updating break"
            );
          }

          setAlert({
            open: true,
            message: "Break updated successfully!",
            severity: "success",
          });

          // Delay closing the drawer so user can see the success message
          setTimeout(() => {
            resetForm();
            if (editMode && onClose) {
              onClose();
            } else {
              setState({ right: false });
            }
            if (typeof onBreakAdded === "function") {
              onBreakAdded();
            }
          }, 2000);

          return; // Don't execute the code below
        } else {
          // Create new break
          console.log("➕ Creating new break");
          response = await axiosInstance.post("/graphql", {
            query: `
              mutation CreateBreak($input: CreateBreakInput!) {
                createBreak(input: $input) {
                  id
                  name
                  breakDate
                  breakTime
                  duration
                  description
                }
              }
            `,
            variables: {
              input: {
                eventId,
                name: values.name,
                breakDate: formattedDate,
                breakTime: formattedTime,
                duration: parseInt(values.duration),
                description: values.description,
                trackIds: values.tracks.map((track) => track.id),
                tags: values.tags,
              },
            },
          });

          if (response.data?.errors) {
            throw new Error(
              response.data.errors[0]?.message || "Error creating break"
            );
          }

          setAlert({
            open: true,
            message: "Break added successfully!",
            severity: "success",
          });
        }

        // For new break creation, close immediately
        resetForm();
        if (editMode && onClose) {
          onClose();
        } else {
          setState({ right: false });
        }
        if (typeof onBreakAdded === "function") {
          onBreakAdded();
        }
      } catch (error) {
        console.error("Break creation failed:", error);
        setAlert({
          open: true,
          message: error.message || "Failed to create break",
          severity: "error",
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Trigger validation when eventData is loaded
  useEffect(() => {
    if (eventData && formik) {
      console.log("🔄 Break - Event data loaded, triggering form validation");
      // Force validation by touching all fields if they have values
      if (formik.values.breakDate) {
        formik.setFieldTouched("breakDate", true);
        formik.validateField("breakDate");
      }
      if (formik.values.breakTime) {
        formik.setFieldTouched("breakTime", true);
        formik.validateField("breakTime");
      }
      if (formik.values.duration) {
        formik.setFieldTouched("duration", true);
        formik.validateField("duration");
      }
    }
  }, [eventData]);

  // Validation with proper business hours check
  const checkAllValidations = (breakDate, breakTime) => {
    if (!eventData || !breakDate || !breakTime) {
      return null;
    }

    // Simple validation using just hours and minutes
    const breakHours = breakTime.getHours();
    const breakMinutes = breakTime.getMinutes();

    // Convert break time to total minutes for easy comparison
    const breakTotalMinutes = breakHours * 60 + breakMinutes;

    // Business hours: 10:00 AM (600 minutes) to 6:00 PM (1080 minutes)
    const businessStart = 10 * 60; // 10:00 AM = 600 minutes
    const businessEnd = 18 * 60; // 6:00 PM = 1080 minutes

    console.log("Simple validation:", {
      breakTime: `${breakHours}:${breakMinutes.toString().padStart(2, "0")}`,
      breakTotalMinutes,
      businessStart,
      businessEnd,
      isValid:
        breakTotalMinutes >= businessStart && breakTotalMinutes < businessEnd,
    });

    // Check if break is within business hours
    if (breakTotalMinutes < businessStart) {
      return {
        conflictType: "daily_hours",
        message: `Break time must be after 10:00 AM`,
      };
    }

    if (breakTotalMinutes >= businessEnd) {
      return {
        conflictType: "daily_hours",
        message: `Break time must be before 06:00 PM`,
      };
    }

    // Check session conflicts - simple approach
    const breakHour = breakTime.getHours();
    const breakMinute = breakTime.getMinutes();

    for (const session of sessions) {
      // Check if session is on same date
      let sessionDate;
      if (
        typeof session.sessionDate === "string" &&
        /^\d+$/.test(session.sessionDate)
      ) {
        sessionDate = new Date(parseInt(session.sessionDate, 10));
      } else {
        sessionDate = new Date(session.sessionDate);
      }

      if (sessionDate.toDateString() !== breakDate.toDateString()) {
        continue;
      }

      // Session time is stored as UTC, convert to local hours
      const [sessionUTCHours, sessionUTCMinutes] = session.sessionTime
        .split(":")
        .map(Number);
      const sessionUTCDate = new Date();
      sessionUTCDate.setUTCHours(sessionUTCHours, sessionUTCMinutes, 0, 0);

      const sessionLocalHours = sessionUTCDate.getHours();
      const sessionLocalMinutes = sessionUTCDate.getMinutes();
      const sessionEndMinutes = sessionLocalMinutes + session.duration;
      let sessionEndHours = sessionLocalHours;

      if (sessionEndMinutes >= 60) {
        sessionEndHours += Math.floor(sessionEndMinutes / 60);
      }

      // Check if break time conflicts with session
      const breakTotalMinutes = breakHour * 60 + breakMinute;
      const sessionStartTotalMinutes =
        sessionLocalHours * 60 + sessionLocalMinutes;
      const sessionEndTotalMinutes =
        sessionEndHours * 60 + (sessionEndMinutes % 60);

      if (
        breakTotalMinutes >= sessionStartTotalMinutes &&
        breakTotalMinutes < sessionEndTotalMinutes
      ) {
        return {
          conflictType: "session",
          sessionName: session.name,
          sessionStart: `${sessionLocalHours
            .toString()
            .padStart(2, "0")}:${sessionLocalMinutes
            .toString()
            .padStart(2, "0")}`,
          sessionEnd: `${sessionEndHours.toString().padStart(2, "0")}:${(
            sessionEndMinutes % 60
          )
            .toString()
            .padStart(2, "0")}`,
        };
      }
    }

    return null;
  };

  // State for real-time validation errors
  const [realTimeErrors, setRealTimeErrors] = useState({});

  // Real-time validation when break date or time changes
  useEffect(() => {
    const { breakDate, breakTime } = formik.values;

    if (breakDate && breakTime) {
      const validation = checkAllValidations(breakDate, breakTime);

      if (validation) {
        let errorMessage = "";
        if (validation.conflictType === "daily_hours") {
          errorMessage = validation.message;
        } else if (validation.conflictType === "session") {
          errorMessage = `Break conflicts with session "${validation.sessionName}" (${validation.sessionStart} - ${validation.sessionEnd})`;
        }
        setRealTimeErrors({ breakTime: errorMessage });
      } else {
        setRealTimeErrors({ breakTime: null });
      }
    } else {
      setRealTimeErrors({ breakTime: null });
    }
  }, [formik.values.breakDate, formik.values.breakTime, sessions, eventData]);

  return (
    <React.Fragment>
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
        >
          {alert.message}
        </Alert>
      </Snackbar>

      {!editMode && (
        <Button
          variant="contained"
          color="inherit"
          onClick={toggleDrawer("right", true)}
          style={{ marginRight: "1rem" }}
        >
          <AddIcon />
          Break
        </Button>
      )}
      <Drawer
        anchor={"right"}
        open={editMode ? true : state["right"]}
        onClose={editMode ? onClose : toggleDrawer("right", false)}
      >
        <Box sx={{ width: "600px", padding: "20px" }} role="presentation">
          <Typography variant="h3" gutterBottom>
            {editMode ? "Edit Break" : "Add Break"}
          </Typography>

          {/* Event Constraints Display */}
          {dataLoading ? (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: "grey.100",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "grey.300",
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Loading Event Schedule Constraints...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait while we load the event details and session data.
              </Typography>
            </Box>
          ) : eventData ? (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: "primary.light",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "primary.main",
                color: "primary.contrastText",
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Event Schedule Constraints
              </Typography>
              <Typography variant="body2">
                📅 <strong>Event Dates:</strong>{" "}
                {parseEventDate(eventData.startDate)?.toLocaleDateString()} -{" "}
                {parseEventDate(eventData.endDate)?.toLocaleDateString()}
              </Typography>
              <Typography variant="body2">
                ⏰ <strong>Daily Hours:</strong>{" "}
                {(() => {
                  // Convert UTC time to event timezone
                  const utcStartTimeStr = eventData.startTime?.includes("T")
                    ? eventData.startTime
                    : `1970-01-01T${eventData.startTime}Z`;
                  const utcStartTime = new Date(utcStartTimeStr);
                  return utcStartTime.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                    timeZone: eventData.timezone || "Asia/Kolkata",
                  });
                })()}{" "}
                -{" "}
                {(() => {
                  // Convert UTC time to event timezone
                  const utcEndTimeStr = eventData.endTime?.includes("T")
                    ? eventData.endTime
                    : `1970-01-01T${eventData.endTime}Z`;
                  const utcEndTime = new Date(utcEndTimeStr);
                  return utcEndTime.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                    timeZone: eventData.timezone || "Asia/Kolkata",
                  });
                })()}{" "}
                ({eventData.timezone})
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
                💡 Breaks must be scheduled within these constraints and cannot
                overlap with sessions
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontSize: "0.85em" }}>
                📊 Sessions loaded: {sessions.length} | Real-time validation:
                Active
              </Typography>

              {/* Real-time selection display */}
              {(formik.values.breakDate || formik.values.breakTime) && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    bgcolor: "rgba(255,255,255,0.1)",
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    gutterBottom
                    sx={{ color: "inherit" }}
                  >
                    🔍 Your Selection Analysis:
                  </Typography>

                  {formik.values.breakDate && (
                    <Typography variant="body2" sx={{ fontSize: "0.9em" }}>
                      📅 <strong>Selected Date:</strong>{" "}
                      {formik.values.breakDate.toLocaleDateString()}
                      {(() => {
                        const breakDate = new Date(formik.values.breakDate);
                        const eventStartDate = parseEventDate(
                          eventData.startDate
                        );
                        const eventEndDate = parseEventDate(eventData.endDate);

                        eventStartDate.setHours(0, 0, 0, 0);
                        eventEndDate.setHours(23, 59, 59, 999);
                        breakDate.setHours(0, 0, 0, 0);

                        const isDateValid =
                          breakDate >= eventStartDate &&
                          breakDate <= eventEndDate;
                        return (
                          <span style={{ marginLeft: "8px" }}>
                            {isDateValid
                              ? "✅ Valid"
                              : "❌ Outside event range"}
                          </span>
                        );
                      })()}
                    </Typography>
                  )}

                  {formik.values.breakTime && (
                    <>
                      <Typography variant="body2" sx={{ fontSize: "0.9em" }}>
                        ⏰ <strong>Selected Time:</strong>{" "}
                        {formik.values.breakTime.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          timeZone: eventData.timezone || "Asia/Kolkata",
                        })}{" "}
                        (Local: {formik.values.breakTime.toLocaleTimeString()})
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.85em", mt: 0.5 }}
                      >
                        🔄 <strong>For Storage:</strong>{" "}
                        {(() => {
                          // This shows how the time will actually be stored - as UTC time extracted from local time
                          const localDateTime = new Date(
                            formik.values.breakDate
                          );
                          localDateTime.setHours(
                            formik.values.breakTime.getHours(),
                            formik.values.breakTime.getMinutes(),
                            formik.values.breakTime.getSeconds(),
                            0
                          );
                          return localDateTime
                            .toISOString()
                            .split("T")[1]
                            .split(".")[0];
                        })()}{" "}
                        UTC (converted from local time)
                      </Typography>

                      {formik.values.breakDate && (
                        <>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.85em", mt: 0.5 }}
                          >
                            📊 <strong>Sessions on this date:</strong>{" "}
                            {(() => {
                              const breakDateObj = new Date(
                                formik.values.breakDate
                              );
                              const sessionsOnDate = sessions.filter(
                                (session) => {
                                  // Parse session date - handle both timestamp and date string formats
                                  let sessionDate;
                                  if (
                                    typeof session.sessionDate === "string" &&
                                    /^\d+$/.test(session.sessionDate)
                                  ) {
                                    // It's a timestamp string, convert to number first
                                    sessionDate = new Date(
                                      parseInt(session.sessionDate, 10)
                                    );
                                  } else {
                                    // It's already a date or date string
                                    sessionDate = new Date(session.sessionDate);
                                  }

                                  console.log("🗓️ Session date comparison:", {
                                    sessionName: session.name,
                                    rawSessionDate: session.sessionDate,
                                    parsedSessionDate:
                                      sessionDate.toDateString(),
                                    breakDate: breakDateObj.toDateString(),
                                    matches:
                                      sessionDate.toDateString() ===
                                      breakDateObj.toDateString(),
                                  });

                                  return (
                                    sessionDate.toDateString() ===
                                    breakDateObj.toDateString()
                                  );
                                }
                              );

                              if (sessionsOnDate.length === 0) {
                                return (
                                  <span style={{ color: "#4caf50" }}>
                                    None found ✅
                                  </span>
                                );
                              }

                              return (
                                <Box
                                  component="span"
                                  sx={{ display: "block", mt: 0.5 }}
                                >
                                  {sessionsOnDate.map((session, index) => {
                                    const [sessionHours, sessionMinutes] =
                                      session.sessionTime
                                        .split(":")
                                        .map(Number);
                                    const sessionTimeUTC = new Date();
                                    sessionTimeUTC.setUTCHours(
                                      sessionHours,
                                      sessionMinutes,
                                      0,
                                      0
                                    );

                                    const sessionStart = new Date(breakDateObj);
                                    sessionStart.setUTCHours(
                                      sessionHours,
                                      sessionMinutes,
                                      0,
                                      0
                                    );

                                    const sessionEnd = new Date(sessionStart);
                                    sessionEnd.setMinutes(
                                      sessionStart.getMinutes() +
                                        session.duration
                                    );

                                    return (
                                      <Typography
                                        key={index}
                                        variant="body2"
                                        sx={{ fontSize: "0.8em", ml: 1 }}
                                      >
                                        • {session.name}:{" "}
                                        {sessionStart.toLocaleTimeString([], {
                                          hour: "2-digit",
                                          minute: "2-digit",
                                          timeZone:
                                            eventData.timezone ||
                                            "Asia/Kolkata",
                                        })}{" "}
                                        -{" "}
                                        {sessionEnd.toLocaleTimeString([], {
                                          hour: "2-digit",
                                          minute: "2-digit",
                                          timeZone:
                                            eventData.timezone ||
                                            "Asia/Kolkata",
                                        })}{" "}
                                        ({session.duration}min)
                                      </Typography>
                                    );
                                  })}
                                </Box>
                              );
                            })()}
                          </Typography>

                          {realTimeErrors.breakTime && (
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.85em",
                                mt: 0.5,
                                color: "#f44336",
                                fontWeight: "bold",
                              }}
                            >
                              ⚠️ <strong>Error:</strong>{" "}
                              {realTimeErrors.breakTime}
                            </Typography>
                          )}

                          {!realTimeErrors.breakTime &&
                            formik.values.breakTime && (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "0.85em",
                                  mt: 0.5,
                                  color: "#4caf50",
                                  fontWeight: "bold",
                                }}
                              >
                                ✅ <strong>Status:</strong> Time is valid and
                                available
                              </Typography>
                            )}
                        </>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: "error.light",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "error.main",
                color: "error.contrastText",
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                ⚠️ Event Data Missing
              </Typography>
              <Typography variant="body2">
                Unable to load event constraints. Please close and reopen this
                form.
              </Typography>
            </Box>
          )}

          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  id="name"
                  name="name"
                  label="Name"
                  fullWidth
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item xs={4}>
                <DatePicker
                  label="Break Date"
                  value={formik.values.breakDate}
                  onChange={(value) => {
                    console.log("📅 Break DatePicker onChange:", value);
                    formik.setFieldValue("breakDate", value);
                  }}
                  minDate={(() => {
                    if (eventData) {
                      const minDate = parseEventDate(eventData.startDate);
                      console.log(
                        "🗓️ Break DatePicker minDate:",
                        minDate,
                        "from:",
                        eventData.startDate
                      );
                      return minDate;
                    }
                    return undefined;
                  })()}
                  maxDate={(() => {
                    if (eventData) {
                      const maxDate = parseEventDate(eventData.endDate);
                      console.log(
                        "🗓️ Break DatePicker maxDate:",
                        maxDate,
                        "from:",
                        eventData.endDate
                      );
                      return maxDate;
                    }
                    return undefined;
                  })()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error:
                        formik.touched.breakDate &&
                        Boolean(formik.errors.breakDate),
                      helperText:
                        (formik.touched.breakDate && formik.errors.breakDate) ||
                        "Select date within event range",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TimePicker
                  label="Break Time"
                  value={formik.values.breakTime}
                  onChange={(value) => formik.setFieldValue("breakTime", value)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error:
                        Boolean(realTimeErrors.breakTime) ||
                        (formik.touched.breakTime &&
                          Boolean(formik.errors.breakTime)),
                      helperText:
                        realTimeErrors.breakTime ||
                        (formik.touched.breakTime && formik.errors.breakTime) ||
                        "Select time within daily hours",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  id="duration"
                  name="duration"
                  label="Duration (Minutes)"
                  fullWidth
                  value={formik.values.duration}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.duration && Boolean(formik.errors.duration)
                  }
                  helperText={formik.touched.duration && formik.errors.duration}
                  type="number"
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="description"
                  name="description"
                  label="Description"
                  multiline
                  rows={4}
                  fullWidth
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.description &&
                    Boolean(formik.errors.description)
                  }
                  helperText={
                    formik.touched.description && formik.errors.description
                  }
                />
              </Grid>
              <AvailableTracks
                eventId={eventId}
                onTrackSelect={(tracks) =>
                  formik.setFieldValue("tracks", tracks)
                }
              />
              <BreakTags
                onTagChange={(tags) => formik.setFieldValue("tags", tags)}
              />
            </Grid>
            <DialogActions>
              <Button
                onClick={editMode ? onClose : () => setState({ right: false })}
                color="primary"
                disabled={formik.isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={
                  formik.isSubmitting ||
                  !eventData ||
                  dataLoading ||
                  Object.values(realTimeErrors).some((error) => error !== null)
                }
              >
                {formik.isSubmitting
                  ? "Submitting..."
                  : dataLoading
                  ? "Loading data..."
                  : !eventData
                  ? "Event data missing..."
                  : Object.values(realTimeErrors).some(
                      (error) => error !== null
                    )
                  ? `Session Conflict Detected`
                  : editMode
                  ? "Update Break"
                  : "Done"}
              </Button>
            </DialogActions>
          </form>
        </Box>
      </Drawer>
    </React.Fragment>
  );
}

export default Break;
